// Marks drawn *over* the pieces: legal-move dots and capture rings, hover,
// keyboard focus, the AFK hint pulse, and the piece being dragged.
import {
  CAPTURE_RING_WIDTH, DRAG_SCALE, GHOST_PIECE_ALPHA, HOVER_GLOW_BLUR, LEGAL_DOT_RADIUS, SQUARE_SIZE,
} from '../../utils/constants.js';
import { isLightSquare, squareToCanvasCenter, squareToCanvasRect, type Point, type ViewerRotation } from '../geometry.js';
import type { RenderPalette } from '../palette.js';
import { drawPiece } from '../PieceRenderer.js';
import type { Square } from '../../game/types.js';

export interface LegalTarget {
  readonly square: Square;
  readonly capture: boolean;
}

export interface InteractionSnapshot {
  readonly hovered: Square | null;
  readonly legalTargets: readonly LegalTarget[];
  /** Piece under the pointer and where it came from. */
  readonly drag: { readonly code: number; readonly from: Square; readonly at: Point } | null;
  /** Keyboard cursor, drawn only while the canvas has focus. */
  readonly focus: Square | null;
  /** AFK assist: a legal move to pulse. */
  readonly hint: { readonly from: Square; readonly to: Square } | null;
  readonly timeMs: number;
}

const HINT_PULSE_PERIOD_MS = 1100;
const FOCUS_INSET = 3;

function drawTargets(ctx: CanvasRenderingContext2D, snap: InteractionSnapshot, palette: RenderPalette, rotation: ViewerRotation): void {
  ctx.save();
  ctx.lineWidth = CAPTURE_RING_WIDTH;
  for (const target of snap.legalTargets) {
    const { x, y } = squareToCanvasCenter(target.square, rotation);
    const color = isLightSquare(target.square) ? palette.legalDot : palette.legalDotOnDark;
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.beginPath();
    if (target.capture) {
      ctx.arc(x, y, SQUARE_SIZE / 2 - CAPTURE_RING_WIDTH / 2, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.arc(x, y, LEGAL_DOT_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

function drawHover(ctx: CanvasRenderingContext2D, sq: Square, palette: RenderPalette, rotation: ViewerRotation): void {
  const r = squareToCanvasRect(sq, rotation);
  ctx.save();
  ctx.shadowBlur = HOVER_GLOW_BLUR;
  ctx.shadowColor = palette.hoverGlow;
  ctx.strokeStyle = palette.hoverGlow;
  ctx.lineWidth = 2;
  ctx.strokeRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
  ctx.restore();
}

function drawFocus(ctx: CanvasRenderingContext2D, sq: Square, palette: RenderPalette, rotation: ViewerRotation): void {
  const r = squareToCanvasRect(sq, rotation);
  ctx.save();
  ctx.strokeStyle = palette.focusRing;
  ctx.lineWidth = 3;
  ctx.setLineDash([6, 4]);
  ctx.strokeRect(r.x + FOCUS_INSET, r.y + FOCUS_INSET, r.w - 2 * FOCUS_INSET, r.h - 2 * FOCUS_INSET);
  ctx.restore();
}

function drawHint(ctx: CanvasRenderingContext2D, hint: { from: Square; to: Square }, timeMs: number, palette: RenderPalette, rotation: ViewerRotation): void {
  const phase = (timeMs % HINT_PULSE_PERIOD_MS) / HINT_PULSE_PERIOD_MS;
  const pulse = 0.5 - 0.5 * Math.cos(phase * Math.PI * 2);
  ctx.save();
  ctx.strokeStyle = palette.selectedRing;
  ctx.globalAlpha = 0.35 + pulse * 0.5;
  ctx.lineWidth = 3;
  for (const sq of [hint.from, hint.to]) {
    const r = squareToCanvasRect(sq, rotation);
    ctx.strokeRect(r.x + 2, r.y + 2, r.w - 4, r.h - 4);
  }
  ctx.restore();
}

export function drawInteractionOverlay(
  ctx: CanvasRenderingContext2D,
  snap: InteractionSnapshot,
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  drawTargets(ctx, snap, palette, rotation);
  if (snap.hint !== null) drawHint(ctx, snap.hint, snap.timeMs, palette, rotation);
  if (snap.hovered !== null && snap.drag === null) drawHover(ctx, snap.hovered, palette, rotation);
  if (snap.focus !== null) drawFocus(ctx, snap.focus, palette, rotation);
  if (snap.drag !== null) {
    const src = squareToCanvasCenter(snap.drag.from, rotation);
    drawPiece(ctx, src.x, src.y, snap.drag.code, palette, { alpha: GHOST_PIECE_ALPHA });
    drawPiece(ctx, snap.drag.at.x, snap.drag.at.y, snap.drag.code, palette, { scale: DRAG_SCALE });
  }
}
